import type { SeriesLineOptions } from "highcharts";
import type { SelectedQuantity, QUANTITY_COLOR_INDEX } from "../../useSelectedQuantities";
import { quantityColor } from "../../useSelectedQuantities";
import { getAxisId, isMultiAxis } from "../hooks/useQuantityYAxes";
import type { MeasurePointMetadata } from "../PointMetadata";
import { MEASURE_POINT_METADATA } from "../PointMetadata";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { color, lightest } from "@ui";
import t from "../../../../i18n";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { isActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Analysis } from "../../../../api/Analysis";
import type { MeasuresChartType } from "../../Measures/MeasuresChartType";
import { createAnalysisFilterZones } from "./createAnalysisFilterZones";
import { INVALID_STATUSES, MetrologyStatus } from "diagnostics-domain/quantity/metrology";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import type { QuantityIndex } from "../../../../api/useQuantities";
import {
  getAnalysisCrossInvalidityEvaluatedQuantityId,
  getAnalysisQuantityMetrologyStatus,
} from "../../metrology";

export const INVALID_COLOR = color.raw.oxygen400;

const drawNoValidation = (colorIndex: QUANTITY_COLOR_INDEX) => ({
  color: undefined,
  marker: {
    enabled: false,
    fillColor: lightest(quantityColor(colorIndex)),
  },
  description: undefined,
});
const drawInvalidAuto = (displayInvalidPoints: boolean) => ({
  color: INVALID_COLOR,
  marker: displayInvalidPoints
    ? { fillColor: INVALID_COLOR, enabled: true }
    : { fillColor: undefined, enabled: true },
  description: t("measures.chart.tooltip.description.invalid_auto"),
});
const drawInvalidAutoCrossFilter = (
  quantityName: string | null,
  displayInvalidPoints: boolean,
) => ({
  color: INVALID_COLOR,
  marker: displayInvalidPoints
    ? { fillColor: INVALID_COLOR, enabled: true }
    : { fillColor: undefined, enabled: true },
  description: t(
    "measures.chart.tooltip.description.invalid_auto_cross_filter",
    quantityName ?? "",
  ),
});
const drawInvalidManual = (displayInvalidPoints: boolean) => ({
  color: INVALID_COLOR,
  marker: displayInvalidPoints
    ? {
        fillColor: INVALID_COLOR,
        enabled: true,
        symbol: "diamond",
      }
    : {
        fillColor: undefined,
        enabled: true,
        symbol: "circle",
      },
  description: t("measures.chart.tooltip.description.invalid_manual"),
});

const drawValidAuto = (colorIndex: QUANTITY_COLOR_INDEX) => ({
  color: undefined,
  marker: { fillColor: quantityColor(colorIndex) },
  description: t("measures.chart.tooltip.description.valid_auto"),
});
const drawValidManual = (colorIndex: QUANTITY_COLOR_INDEX, displayInvalidPoints: boolean) => ({
  color: undefined,
  marker: displayInvalidPoints
    ? {
        fillColor: quantityColor(colorIndex),
        symbol: "diamond",
      }
    : {
        fillColor: undefined,
        symbol: "circle",
      },
  description: t("measures.chart.tooltip.description.valid_manual"),
});

const drawValidPartialInputs = (
  colorIndex: QUANTITY_COLOR_INDEX,
  inputCount: number,
  missingSmartArgsCount: number,
) => ({
  color: undefined,
  marker: {
    enabled: true,
    symbol: "circle",
    fillColor: color.darkNitrogen50,
    lineColor: quantityColor(colorIndex),
    lineWidth: 2,
  },
  description: t(
    "measures.chart.tooltip.description.valid_partial_inputs",
    inputCount,
    missingSmartArgsCount,
  ),
});

const drawInvalidInherited = (displayInvalidPoints: boolean) => ({
  color: INVALID_COLOR,
  marker: displayInvalidPoints
    ? {
        fillColor: INVALID_COLOR,
        enabled: true,
      }
    : {
        fillColor: undefined,
        enabled: true,
      },
  description: t("measures.chart.tooltip.description.invalid_herited"),
});

const drawMode = {
  ["NO_VALIDATION"]: drawNoValidation,
  ["INVALID_AUTO"]: drawInvalidAuto,
  ["INVALID_AUTO_CROSS_FILTER"]: drawInvalidAutoCrossFilter,
  ["INVALID_MANUAL"]: drawInvalidManual,
  ["VALID_AUTO"]: drawValidAuto,
  ["VALID_MANUAL"]: drawValidManual,
  ["INVALID_INHERITED"]: drawInvalidInherited,
  ["VALID_PARTIAL_INPUTS"]: drawValidPartialInputs,
};

function drawModeFunction(
  status: MetrologyStatus,
  colorIndex: QUANTITY_COLOR_INDEX,
  quantityName: string | null,
  displayInvalidPoints: boolean,
  inputCount: number,
  missingSmartArgsCount: number,
) {
  if ("VALID_PARTIAL_INPUTS" === status)
    return drawMode[status](colorIndex, inputCount, missingSmartArgsCount);

  if ("NO_VALIDATION" === status || "VALID_AUTO" === status || "VALID_MANUAL" === status)
    return drawMode[status](colorIndex, displayInvalidPoints);

  if ("INVALID_AUTO" === status || "INVALID_MANUAL" === status || "INVALID_INHERITED" === status)
    return drawMode[status](displayInvalidPoints);

  if ("INVALID_AUTO_CROSS_FILTER" === status)
    return drawMode[status](quantityName, displayInvalidPoints);

  return {};
}

export function getPoints(
  quantityIndex: QuantityIndex,
  analyses: (Analysis | ActiveAnalysis)[],
  selectedQuantity: SelectedQuantity,
  symptomMode: boolean,
  selectedChartOption: MeasuresChartType,
  _metrologyOverrides: MetrologyOverrides,
  displayInvalidPoints: boolean,
) {
  return analyses.reduce((pointArray: Highcharts.PointOptionsObject[], analysis, index) => {
    const previousAnalysis = analyses[index - 1];
    const { quantity, colorIndex } = selectedQuantity;
    const quantityValue = analysis.quantityValues[quantity.id];

    // missing measured value
    if (!quantityValue) {
      const pointMetadata: MeasurePointMetadata = {
        type: MEASURE_POINT_METADATA,
        human: null,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        date: analysis.date,
        quantityId: quantity.id,
        quantityName: quantity.name,
        validity: "NO_VALIDATION",
        publishedAnalysis: false,
      };
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: null,
        custom: pointMetadata,
      });
      return pointArray;
    }

    // middle point between two cycles
    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      pointArray.push({
        id: `cycle-middlepoint-${previousAnalysis.group}-${analysis.group}`,
        x: (analysis.date + previousAnalysis.date) / 2,
        y: null,
        allowPointSelect: false,
      });
    }

    const metrologyStatus = isActiveAnalysis(analysis)
      ? getAnalysisQuantityMetrologyStatus(analysis, quantity)
      : null;

    const pointMetadata: MeasurePointMetadata = {
      type: MEASURE_POINT_METADATA,
      group: analysis.group,
      label: analysis.label,
      analysisId: analysis.id,
      date: analysis.date,
      human:
        quantityValue.measured === null
          ? null
          : {
              value: quantityValue.measured,
              unit: quantity.unit,
              precision: 2,
              sign: null,
            },
      validity: metrologyStatus ?? "NO_VALIDATION",
      publishedAnalysis: isActiveAnalysis(analysis) ? analysis.isPublished : false,
      quantityId: quantity.id,
      quantityName: quantity.name,
    };

    // invalid value displayed
    if (
      !displayInvalidPoints &&
      (metrologyStatus ? INVALID_STATUSES.has(metrologyStatus) : false)
    ) {
      pointArray.push({
        id: `analysis-id-${analysis.id}-invalid-hidden`,
        x: analysis.date,
        y: null,
        custom: pointMetadata,
      });
      return pointArray;
    }

    const { measured, std } = quantityValue;
    const isStd = selectedChartOption === "std";

    const isSymptomMissing =
      symptomMode && (!("simulated" in quantityValue) || quantityValue.simulated === null);

    // outdated, missing simulated value in symptom mode or missing std value
    if (isSymptomMissing || (isStd && std === null)) {
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: null,
        custom: pointMetadata,
        className: quantity.name, // testing puposes
        description: t("measures.chart.tooltip.description.missingValue"),
      });
      return pointArray;
    }

    const crossInvalidityEvaluatedQuantityId = isActiveAnalysis(analysis)
      ? getAnalysisCrossInvalidityEvaluatedQuantityId(analysis, quantity)
      : null;
    const crossInvalidityName = crossInvalidityEvaluatedQuantityId
      ? (quantityIndex[crossInvalidityEvaluatedQuantityId]?.name ?? null)
      : null;

    const pointDrawing = drawModeFunction(
      metrologyStatus ?? "NO_VALIDATION",
      colorIndex,
      crossInvalidityName,
      displayInvalidPoints,
      quantityValue.inputCount ?? 0,
      quantityValue.missingSmartArgsCount ?? 0,
    );

    // base
    pointArray.push({
      id: `analysis-id-${analysis.id}`,
      x: analysis.date,
      y: isStd ? std : measured,
      custom: pointMetadata,
      className: quantity.name, // testing puposes
      ...pointDrawing,
    });

    return pointArray;
  }, []);
}

function getMeasureSeries({
  quantityIndex,
  analyses,
  activationFilters,
  selectedQuantities,
  axisGroupedByUnit,
  actionAllowed,
  symptomMode,
  selectedChartOption,
  metrologyOverrides,
  displayInvalidPoints,
}: {
  quantityIndex: QuantityIndex;
  analyses: (Analysis | ActiveAnalysis)[];
  activationFilters?: {
    quantityId: string;
    min: number | null;
    max: number | null;
    maxStd: number | null;
  }[];
  selectedQuantities: SelectedQuantity[];
  axisGroupedByUnit: boolean;
  actionAllowed: boolean;
  symptomMode: boolean;
  selectedChartOption: MeasuresChartType;
  metrologyOverrides: MetrologyOverrides;
  displayInvalidPoints: boolean;
}) {
  const multiAxis = isMultiAxis(selectedQuantities, axisGroupedByUnit);

  return selectedQuantities.map((selectedQuantity: SelectedQuantity) => ({
    id: `measure-serie-${selectedQuantity.quantity.name}`,
    name: selectedQuantity.quantity.name,
    data: getPoints(
      quantityIndex,
      analyses,
      selectedQuantity,
      symptomMode,
      selectedChartOption,
      metrologyOverrides,
      displayInvalidPoints,
    ),
    zones: activationFilters
      ? createAnalysisFilterZones(selectedQuantity, activationFilters, selectedChartOption)
      : [],
    type: "line",
    allowPointSelect: actionAllowed,
    cursor: actionAllowed ? "pointer" : "default",
    replicateInNavigator: true,
    yAxis: getAxisId(selectedQuantity.quantity, axisGroupedByUnit, multiAxis),
    color: quantityColor(selectedQuantity.colorIndex),
    marker: {
      symbol: "circle",
      lineColor: undefined,
      lineWidth: 2,
      states: {
        select: {
          radius: 8,
          fillColor: undefined, // use current
          lineColor: undefined, // use current
          lineWidth: 4,
        },
      },
    },
  })) as NonEmptyArray<SeriesLineOptions>;
}

export default getMeasureSeries;
